import { useEffect, useRef, useCallback } from "react";
import { sampleWind, makeWindConfig } from "./windField";

// ─── Leaf/Petal types ─────────────────────────────────────────────────────
const LEAF_TYPES = [
  { name: "petal", draw: drawPetal, aspect: 0.6, mass: 0.001, area: 14 },
  { name: "oval", draw: drawOvalLeaf, aspect: 0.55, mass: 0.002, area: 18 },
  { name: "small_petal", draw: drawSmallPetal, aspect: 0.5, mass: 0.0008, area: 8 },
];

// Cut-paper shapes: flat fills (no radial gradients) so each particle reads as a
// piece of coloured paper. color2 is used only as a darker "fold" accent.
function drawPetal(ctx, size, color1, color2) {
  ctx.save();
  const s = size;
  ctx.beginPath();
  ctx.moveTo(0, -s);
  ctx.bezierCurveTo(s * 0.8, -s * 0.6, s * 0.7, s * 0.3, 0, s * 0.6);
  ctx.bezierCurveTo(-s * 0.7, s * 0.3, -s * 0.8, -s * 0.6, 0, -s);
  ctx.closePath();
  ctx.fillStyle = color1;
  ctx.fill();
  // A folded paper crease down one side
  ctx.beginPath();
  ctx.moveTo(0, -s);
  ctx.bezierCurveTo(-s * 0.7, -s * 0.6, -s * 0.8, -s * 0.6, 0, s * 0.6);
  ctx.closePath();
  ctx.fillStyle = color2;
  ctx.globalAlpha *= 0.35;
  ctx.fill();
  ctx.restore();
}

function drawOvalLeaf(ctx, size, color1, color2) {
  ctx.save();
  const s = size;
  ctx.beginPath();
  ctx.ellipse(0, 0, s * 0.45, s * 0.85, 0, 0, Math.PI * 2);
  ctx.closePath();
  ctx.fillStyle = color1;
  ctx.fill();
  // Center vein (a darker paper crease)
  ctx.strokeStyle = color2;
  ctx.globalAlpha *= 0.5;
  ctx.lineWidth = 0.8;
  ctx.beginPath();
  ctx.moveTo(0, -s * 0.75);
  ctx.lineTo(0, s * 0.75);
  ctx.stroke();
  ctx.restore();
}

function drawSmallPetal(ctx, size, color1, color2) {
  ctx.save();
  const s = size;
  ctx.beginPath();
  ctx.moveTo(0, -s * 0.7);
  ctx.bezierCurveTo(s * 0.5, -s * 0.4, s * 0.45, s * 0.2, 0, s * 0.5);
  ctx.bezierCurveTo(-s * 0.45, s * 0.2, -s * 0.5, -s * 0.4, 0, -s * 0.7);
  ctx.closePath();
  ctx.fillStyle = color1;
  ctx.fill();
  ctx.restore();
}

// ─── Color palette ────────────────────────────────────────────────────────
// Paper Garden palette: leaf greens, marigold, berry, sky. [fill, fold].
const PAPER_PALETTE = [
  ["#4F8A3D", "#356128"], ["#7CB85F", "#4F8A3D"], ["#F2A93B", "#D08713"],
  ["#C84B6A", "#9E2F4B"], ["#5EA9C4", "#3C7E96"], ["#F8C66E", "#F2A93B"],
];

// Reference size used for tuning physics values
const REF_SIZE = 800;

// Maximum number of particles alive at once
const MAX_PARTICLES = 300;

// Depth bucket thresholds — roughly equal thirds of the [0.3, 1.0] range
const DEPTH_MID = 0.53;
const DEPTH_NEAR = 0.77;

// ─── Particle creation ────────────────────────────────────────────────────
function createParticle(width, height, palette) {
  const type = LEAF_TYPES[Math.floor(Math.random() * LEAF_TYPES.length)];
  const colors = palette[Math.floor(Math.random() * palette.length)];
  const sizeScale = Math.max(width, height) / REF_SIZE;
  const size = (8 + Math.random() * 14) * sizeScale;
  // Spawn from left edge or top, biased toward left
  let x, y;
  if (Math.random() < 0.7) {
    x = -20 * sizeScale - Math.random() * 60 * sizeScale;
    y = Math.random() * height;
  } else {
    x = Math.random() * width * 0.5;
    y = -20 * sizeScale - Math.random() * 40 * sizeScale;
  }
  return {
    x, y,
    vx: (20 + Math.random() * 40) * sizeScale,
    vy: (-10 + Math.random() * 20) * sizeScale,
    angle: Math.random() * Math.PI * 2,
    angVel: (Math.random() - 0.5) * 2,
    // "3D" tilt for flutter effect: angle around horizontal axis
    tilt: Math.random() * Math.PI * 2,
    tiltSpeed: 1.5 + Math.random() * 3,
    size,
    type,
    colors,
    mass: type.mass * (size / 12),
    area: type.area * (size / 12) * (size / 12),
    age: 0,
    maxAge: 8 + Math.random() * 12,
    opacity: 0,
    // Unique offset for noise sampling
    noiseOff: Math.random() * 1000,
    // Depth layer (0=far, 1=near) for parallax
    depth: 0.3 + Math.random() * 0.7,
  };
}

// ─── Physics step ─────────────────────────────────────────────────────────
function stepParticle(p, dt, time, windConfig, width, height, containerWidth) {
  p.age += dt;
  // Fade in / out
  if (p.age < 0.5) p.opacity = p.age / 0.5;
  else if (p.age > p.maxAge - 1.5) p.opacity = Math.max(0, (p.maxAge - p.age) / 1.5);
  else p.opacity = 1;

  // Sample wind at particle position, modulated by depth
  const wind = sampleWind(p.x + p.noiseOff, p.y + p.noiseOff, time, windConfig, containerWidth);
  const windX = wind.x * p.depth;
  const windY = wind.y * p.depth;

  // Relative velocity (particle vs wind)
  const relVx = p.vx - windX;
  const relVy = p.vy - windY;
  const relSpeed = Math.sqrt(relVx * relVx + relVy * relVy);

  // Projected area varies with tilt (simulates the "flip" catching more/less air)
  const projectedArea = p.area * (0.3 + 0.7 * Math.abs(Math.cos(p.tilt)));

  // Drag: opposes relative velocity
  const dragCoeff = 0.47;
  const rho = 1.225; // air density
  const dragMag = 0.5 * rho * dragCoeff * projectedArea * 0.0001 * relSpeed;
  const dragX = relSpeed > 0.01 ? -dragMag * (relVx / relSpeed) : 0;
  const dragY = relSpeed > 0.01 ? -dragMag * (relVy / relSpeed) : 0;

  // Lift: perpendicular to relative velocity, proportional to sin(2 * angle_of_attack)
  const aoa = p.tilt % (Math.PI * 2);
  const liftMag = 0.3 * dragMag * Math.sin(2 * aoa);
  const liftX = relSpeed > 0.01 ? liftMag * (-relVy / relSpeed) : 0;
  const liftY = relSpeed > 0.01 ? liftMag * (relVx / relSpeed) : 0;

  // Gravity (reduced by depth for parallax feel)
  const gravity = 60 * p.depth;

  // Accumulate forces -> acceleration
  const ax = (dragX + liftX) / p.mass;
  const ay = (dragY + liftY) / p.mass + gravity;

  // Integrate (semi-implicit Euler)
  p.vx += ax * dt;
  p.vy += ay * dt;
  p.x += p.vx * dt;
  p.y += p.vy * dt;

  // Torque: wind induces spin proportional to angular velocity offset
  const torqueWind = (windX * 0.02) * Math.sin(p.angle);
  const torqueDamp = -p.angVel * 0.3; // angular damping
  p.angVel += (torqueWind + torqueDamp) * dt;
  p.angle += p.angVel * dt;

  // Tilt oscillation (flutter)
  p.tilt += p.tiltSpeed * dt;

  // Alive check — scale boundary margins to container size
  const margin = 100 * (Math.max(width, height) / REF_SIZE);
  return p.age < p.maxAge && p.x < width + margin && p.y < height + margin && p.x > -margin * 2;
}

// ─── Helper: total particle count across all buckets ─────────────────────
function totalParticles(buckets) {
  return buckets[0].length + buckets[1].length + buckets[2].length;
}

// ─── Helper: in-place filter for a single bucket ─────────────────────────
function filterBucketInPlace(bucket, dt, time, windCfg, logicalW, logicalH) {
  let alive = 0;
  for (let i = 0; i < bucket.length; i++) {
    if (stepParticle(bucket[i], dt, time, windCfg, logicalW, logicalH, logicalW)) {
      bucket[alive++] = bucket[i];
    }
  }
  bucket.length = alive;
}

// ─── Helper: draw all particles in a bucket ──────────────────────────────
function drawBucket(ctx, bucket, dpr) {
  for (let i = 0; i < bucket.length; i++) {
    const p = bucket[i];
    if (p.opacity <= 0) continue;
    ctx.save();
    ctx.globalAlpha = p.opacity * (0.75 + p.depth * 0.25);
    ctx.translate(p.x * dpr, p.y * dpr);
    ctx.rotate(p.angle);

    // Simulate 3D tilt by scaling Y (foreshortening)
    const scaleY = 0.3 + 0.7 * Math.abs(Math.cos(p.tilt));
    ctx.scale(dpr, scaleY * dpr);

    // Depth-based size scaling
    const depthScale = 0.5 + p.depth * 0.5;
    ctx.scale(depthScale, depthScale);

    // Subtle warm paper-lift shadow (crisp, low blur)
    ctx.shadowColor = "rgba(58,46,38,0.18)";
    ctx.shadowBlur = 2 * p.depth;
    ctx.shadowOffsetX = 1 * p.depth;
    ctx.shadowOffsetY = 2 * p.depth;

    p.type.draw(ctx, p.size, p.colors[0], p.colors[1]);
    ctx.restore();
  }
}

// ─── Main Component ───────────────────────────────────────────────────────
/**
 * BlowingPetals — canvas-based petal/leaf animation with simplex noise wind.
 *
 * @param {number} density — particles spawned per second. // Reasonable range: 1–15
 * @param {number} windStrength — wind force multiplier. // Reasonable range: 0.1–1.0
 * @param {string} className — additional CSS class for the outer container
 * @param {object} style — additional inline styles for the outer container
 */
export default function BlowingPetals({
  density = 5,        // Reasonable range: 1–15
  windStrength = 0.6, // Reasonable range: 0.1–1.0
  className = "",
  style: externalStyle,
}) {
  const canvasRef = useRef(null);
  const ctxRef = useRef(null);
  // Three depth buckets: [far, mid, near] — replaces single particlesRef + sort
  const bucketsRef = useRef([[], [], []]);
  const timeRef = useRef(0);
  const rafRef = useRef(null);
  const safetyTimeoutRef = useRef(null);
  const lastFrameRef = useRef(0);
  const spawnAccum = useRef(0);
  const sizeRef = useRef({ w: 800, h: 600 });
  const densityRef = useRef(density);
  const windStrengthRef = useRef(windStrength);
  const reducedMotionRef = useRef(false);
  const dprRef = useRef(1);
  const isIntersectingRef = useRef(true);
  const tabVisibleRef = useRef(true);
  const startLoopRef = useRef(null);

  // Keep refs in sync with props
  useEffect(() => { densityRef.current = density; }, [density]);
  useEffect(() => { windStrengthRef.current = windStrength; }, [windStrength]);

  // Check prefers-reduced-motion
  useEffect(() => {
    const mql = window.matchMedia("(prefers-reduced-motion: reduce)");
    reducedMotionRef.current = mql.matches;
    const handler = (e) => {
      reducedMotionRef.current = e.matches;
      if (!e.matches) startLoopRef.current?.();
    };
    mql.addEventListener("change", handler);
    return () => mql.removeEventListener("change", handler);
  }, []);

  const windConfig = useRef(makeWindConfig(windStrength));

  const animate = useCallback((timestamp) => {
    // Clear safety-net timeout since the loop is actively running
    if (safetyTimeoutRef.current) {
      clearTimeout(safetyTimeoutRef.current);
      safetyTimeoutRef.current = null;
    }

    // Stop the loop when user prefers reduced motion or component is not visible
    if (reducedMotionRef.current || !isIntersectingRef.current || !tabVisibleRef.current) {
      rafRef.current = null;
      // Safety-net: re-check in 1s in case an observer event was missed
      if (!safetyTimeoutRef.current) {
        safetyTimeoutRef.current = setTimeout(() => {
          safetyTimeoutRef.current = null;
          startLoopRef.current?.();
        }, 1000);
      }
      return;
    }

    const canvas = canvasRef.current;
    if (!canvas) return;
    const ctx = ctxRef.current;
    if (!ctx) return;
    const { width, height } = canvas;
    const dpr = dprRef.current;
    // Logical dimensions (CSS pixels)
    const logicalW = width / dpr;
    const logicalH = height / dpr;

    const dt = Math.min((timestamp - lastFrameRef.current) / 1000, 0.05);
    lastFrameRef.current = timestamp;
    timeRef.current += dt;
    const time = timeRef.current;

    // Update wind config from current prop ref
    windConfig.current = makeWindConfig(windStrengthRef.current);

    const pal = PAPER_PALETTE;
    const buckets = bucketsRef.current;

    // Spawn new particles (respecting MAX_PARTICLES cap)
    spawnAccum.current += densityRef.current * dt;
    while (spawnAccum.current >= 1) {
      if (totalParticles(buckets) >= MAX_PARTICLES) {
        spawnAccum.current = 0;
        break;
      }
      const p = createParticle(logicalW, logicalH, pal);
      // Place into depth bucket
      if (p.depth < DEPTH_MID) buckets[0].push(p);
      else if (p.depth < DEPTH_NEAR) buckets[1].push(p);
      else buckets[2].push(p);
      spawnAccum.current -= 1;
    }

    // Clear
    ctx.clearRect(0, 0, width, height);

    const wc = windConfig.current;

    // Step, filter, and draw each bucket in depth order (far → mid → near)
    for (let b = 0; b < 3; b++) {
      filterBucketInPlace(buckets[b], dt, time, wc, logicalW, logicalH);
      drawBucket(ctx, buckets[b], dpr);
    }

    // Wind streaks (subtle)
    const ws = windStrengthRef.current;
    const streakCount = Math.floor(ws * 6);
    for (let i = 0; i < streakCount; i++) {
      const sx = ((time * 300 + i * 237) % (logicalW + 200)) - 100;
      const sy = ((i * 173 + time * 40) % logicalH);
      const len = 30 + ws * 60;
      ctx.save();
      ctx.globalAlpha = 0.04 + ws * 0.03;
      ctx.strokeStyle = "#fff";
      ctx.lineWidth = 0.5 * dpr;
      ctx.beginPath();
      ctx.moveTo(sx * dpr, sy * dpr);
      ctx.lineTo((sx + len) * dpr, (sy + len * 0.1) * dpr);
      ctx.stroke();
      ctx.restore();
    }

    rafRef.current = requestAnimationFrame(animate);
  }, []);

  // Restart the animation loop if it was stopped and conditions are now favorable
  startLoopRef.current = () => {
    if (rafRef.current) return; // already running
    if (reducedMotionRef.current || !isIntersectingRef.current || !tabVisibleRef.current) return;
    lastFrameRef.current = performance.now();
    rafRef.current = requestAnimationFrame(animate);
  };

  useEffect(() => {
    const canvas = canvasRef.current;
    if (!canvas) return;

    const resize = () => {
      const container = canvas.parentElement;
      const w = container.clientWidth;
      const h = container.clientHeight;
      const dpr = Math.min(window.devicePixelRatio || 1, 2);
      dprRef.current = dpr;
      canvas.width = w * dpr;
      canvas.height = h * dpr;
      canvas.style.width = w + "px";
      canvas.style.height = h + "px";
      sizeRef.current = { w, h };
    };

    resize();
    ctxRef.current = canvas.getContext("2d");

    // Use ResizeObserver on parent so canvas responds to parent-driven size changes
    const parent = canvas.parentElement;
    const ro = new ResizeObserver(resize);
    ro.observe(parent);

    // IntersectionObserver — pause when scrolled off-screen
    const io = new IntersectionObserver(
      ([entry]) => {
        isIntersectingRef.current = entry.isIntersecting;
        if (entry.isIntersecting) startLoopRef.current?.();
      },
      { threshold: 0 }
    );
    io.observe(parent);

    // Pause when tab is backgrounded
    const onVisibilityChange = () => {
      tabVisibleRef.current = document.visibilityState === "visible";
      if (tabVisibleRef.current) startLoopRef.current?.();
    };
    document.addEventListener("visibilitychange", onVisibilityChange);

    lastFrameRef.current = performance.now();
    rafRef.current = requestAnimationFrame(animate);

    return () => {
      ro.disconnect();
      io.disconnect();
      document.removeEventListener("visibilitychange", onVisibilityChange);
      if (rafRef.current) cancelAnimationFrame(rafRef.current);
      if (safetyTimeoutRef.current) clearTimeout(safetyTimeoutRef.current);
    };
  }, [animate]);

  return (
    <div
      className={className}
      aria-hidden="true"
      style={{
        // Only apply default layout when no className provides its own positioning
        ...(!className && {
          width: "100%",
          height: "100%",
          position: "relative",
          overflow: "hidden",
        }),
        ...externalStyle,
      }}
    >
      <canvas
        ref={canvasRef}
        role="presentation"
        style={{
          position: "absolute",
          top: 0, left: 0,
          width: "100%",
          height: "100%",
          pointerEvents: "none",
        }}
      />
    </div>
  );
}
